import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { toStructuredGraph } from '../../utils/structuredDataGraph';
import { withSiteStructuredData } from '../../utils/structuredDataSchemas';

/**
 * Renders a single JSON-LD script with @graph from schema nodes or a pre-built graph.
 * Accepts either an array of nodes, an object with @graph, or a full document.
 * Organization and WebSite are injected automatically unless includeSiteBase is false.
 * Runtime schema replaces the prerendered head script on hydration (see data-prerendered-schema).
 */
export default function StructuredData({ graph, data, includeSiteBase = true, orgDescription }) {
  useEffect(() => {
    document
      .querySelectorAll('script[type="application/ld+json"][data-prerendered-schema]')
      .forEach((node) => node.remove());
  }, []);

  const jsonLd = useMemo(() => {
    const source = graph ?? data;
    if (!source) {
      return null;
    }
    const enriched = includeSiteBase ? withSiteStructuredData(source, { orgDescription }) : source;
    return JSON.stringify(toStructuredGraph(enriched));
  }, [graph, data, includeSiteBase, orgDescription]);

  if (!jsonLd) {
    return null;
  }

  return (
    <Helmet>
      <script type="application/ld+json">{jsonLd}</script>
    </Helmet>
  );
}
